import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const FILMSTRIP_FRAMES = 12;
const MIN_TRIM_SECONDS = 1;
const HANDLE_HIT_PX = 18;

export type VideoTrimRange = {
  startSeconds: number;
  endSeconds: number;
};

type DragMode = 'start' | 'end' | 'window' | null;

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function formatTrimTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = sec - m * 60;
  if (m > 0) return `${m}:${s.toFixed(1).padStart(4, '0')}`;
  return `${s.toFixed(1)}s`;
}

async function loadVideoElement(src: string, useCors: boolean): Promise<HTMLVideoElement | null> {
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  if (useCors) video.crossOrigin = 'anonymous';

  const ok = await new Promise<boolean>(resolve => {
    let settled = false;
    const done = (success: boolean) => {
      if (settled) return;
      settled = true;
      video.onloadeddata = null;
      video.onloadedmetadata = null;
      video.onerror = null;
      resolve(success);
    };
    video.onloadeddata = () => done(true);
    video.onloadedmetadata = () => {
      if (video.readyState >= 1) done(true);
    };
    video.onerror = () => done(false);
    video.src = src;
  });

  if (!ok || !Number.isFinite(video.duration) || video.duration <= 0) {
    video.removeAttribute('src');
    video.load();
    return null;
  }
  return video;
}

function releaseVideo(video: HTMLVideoElement): void {
  video.removeAttribute('src');
  video.load();
}

async function captureFilmstrip(src: string, count: number): Promise<string[]> {
  // Prefer CORS so canvas frames can be exported; fall back without it (may yield 0 frames if tainted).
  let loaded = await loadVideoElement(src, true);
  if (!loaded) loaded = await loadVideoElement(src, false);
  if (!loaded) return [];
  const video = loaded;

  const duration = video.duration;
  const canvas = document.createElement('canvas');
  const w = 72;
  const h = 128;
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    releaseVideo(video);
    return [];
  }

  const frames: string[] = [];
  const seek = (t: number) =>
    new Promise<void>((resolve, reject) => {
      const onSeeked = () => {
        video.removeEventListener('seeked', onSeeked);
        resolve();
      };
      video.addEventListener('seeked', onSeeked);
      const onErr = () => {
        video.removeEventListener('seeked', onSeeked);
        video.removeEventListener('error', onErr);
        reject(new Error('seek failed'));
      };
      video.addEventListener('error', onErr);
      try {
        video.currentTime = Math.min(Math.max(0, t), Math.max(0, duration - 0.05));
      } catch {
        video.removeEventListener('seeked', onSeeked);
        video.removeEventListener('error', onErr);
        reject(new Error('seek failed'));
      }
    });

  try {
    for (let i = 0; i < count; i++) {
      const t = count === 1 ? 0 : (i / (count - 1)) * Math.max(0, duration - 0.05);
      await seek(t);
      ctx.fillStyle = '#111';
      ctx.fillRect(0, 0, w, h);
      const vw = video.videoWidth || w;
      const vh = video.videoHeight || h;
      const scale = Math.max(w / vw, h / vh);
      const dw = vw * scale;
      const dh = vh * scale;
      try {
        ctx.drawImage(video, (w - dw) / 2, (h - dh) / 2, dw, dh);
        frames.push(canvas.toDataURL('image/jpeg', 0.72));
      } catch {
        // Tainted canvas (no CORS) — stop; trimmer falls back to solid bar.
        break;
      }
    }
  } catch {
    // Partial / empty filmstrip is fine.
  } finally {
    releaseVideo(video);
  }
  return frames;
}

/**
 * Instagram Reels–style trim bar: filmstrip + draggable selection window with side handles.
 */
export default function VideoClipTrimmer({
  src,
  startSeconds,
  endSeconds,
  maxSelectableSeconds,
  onChange,
}: {
  src: string;
  startSeconds: number;
  endSeconds: number;
  /** Max length of the selected window (App Preview budget remaining for this clip). */
  maxSelectableSeconds: number;
  onChange: (range: VideoTrimRange) => void;
}) {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const [duration, setDuration] = useState(0);
  const [frames, setFrames] = useState<string[]>([]);
  const [loadingFrames, setLoadingFrames] = useState(true);
  const dragRef = useRef<{
    mode: DragMode;
    originX: number;
    originStart: number;
    originEnd: number;
  } | null>(null);
  const [dragging, setDragging] = useState<DragMode>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingFrames(true);
    setFrames([]);
    setDuration(0);

    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      if (cancelled) return;
      const d = video.duration;
      if (Number.isFinite(d) && d > 0) setDuration(d);
    };
    video.src = src;

    captureFilmstrip(src, FILMSTRIP_FRAMES)
      .then(f => {
        if (!cancelled) setFrames(f);
      })
      .catch(() => {
        if (!cancelled) setFrames([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingFrames(false);
      });

    return () => {
      cancelled = true;
      video.removeAttribute('src');
      video.load();
    };
  }, [src]);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const resolved = (() => {
    if (!(duration > 0)) {
      return {
        start: Math.max(0, startSeconds),
        end: endSeconds > startSeconds ? endSeconds : startSeconds + MIN_TRIM_SECONDS,
      };
    }
    const maxWin = Math.min(maxSelectableSeconds, duration);
    let start = clamp(startSeconds, 0, Math.max(0, duration - MIN_TRIM_SECONDS));
    let end = endSeconds > start ? endSeconds : Math.min(duration, start + maxWin);
    end = clamp(end, start + MIN_TRIM_SECONDS, duration);
    if (end - start > maxWin) {
      end = start + maxWin;
      if (end > duration) {
        end = duration;
        start = Math.max(0, end - maxWin);
      }
    }
    return { start, end };
  })();

  // Persist only when an explicit end was set but needs clamping (budget/duration).
  useEffect(() => {
    if (!(duration > 0)) return;
    if (!(endSeconds > startSeconds)) return; // unset end → full source until user trims
    if (
      Math.abs(resolved.start - startSeconds) > 0.02 ||
      Math.abs(resolved.end - endSeconds) > 0.02
    ) {
      onChangeRef.current({ startSeconds: resolved.start, endSeconds: resolved.end });
    }
  }, [duration, maxSelectableSeconds, startSeconds, endSeconds, resolved.start, resolved.end]);

  const clientXToTime = useCallback(
    (clientX: number) => {
      const el = trackRef.current;
      if (!el || !(duration > 0)) return 0;
      const rect = el.getBoundingClientRect();
      const ratio = clamp((clientX - rect.left) / Math.max(1, rect.width), 0, 1);
      return ratio * duration;
    },
    [duration]
  );

  const applyDrag = useCallback(
    (clientX: number) => {
      const drag = dragRef.current;
      if (!drag || !(duration > 0)) return;
      const maxWin = Math.min(maxSelectableSeconds, duration);
      const t = clientXToTime(clientX);
      const delta = t - clientXToTime(drag.originX);

      if (drag.mode === 'start') {
        const maxStart = drag.originEnd - MIN_TRIM_SECONDS;
        let start = clamp(drag.originStart + delta, 0, maxStart);
        if (drag.originEnd - start > maxWin) start = drag.originEnd - maxWin;
        onChangeRef.current({ startSeconds: start, endSeconds: drag.originEnd });
        return;
      }
      if (drag.mode === 'end') {
        const minEnd = drag.originStart + MIN_TRIM_SECONDS;
        let end = clamp(drag.originEnd + delta, minEnd, duration);
        if (end - drag.originStart > maxWin) end = drag.originStart + maxWin;
        onChangeRef.current({ startSeconds: drag.originStart, endSeconds: end });
        return;
      }
      if (drag.mode === 'window') {
        const win = drag.originEnd - drag.originStart;
        let start = drag.originStart + delta;
        start = clamp(start, 0, duration - win);
        onChangeRef.current({ startSeconds: start, endSeconds: start + win });
      }
    },
    [clientXToTime, duration, maxSelectableSeconds]
  );

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: PointerEvent) => {
      e.preventDefault();
      applyDrag(e.clientX);
    };
    const onUp = () => {
      dragRef.current = null;
      setDragging(null);
    };
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [dragging, applyDrag]);

  const beginDrag = (mode: DragMode, clientX: number) => {
    if (!mode || !(duration > 0)) return;
    dragRef.current = {
      mode,
      originX: clientX,
      originStart: resolved.start,
      originEnd: resolved.end,
    };
    setDragging(mode);
  };

  const startPct = duration > 0 ? (resolved.start / duration) * 100 : 0;
  const endPct = duration > 0 ? (resolved.end / duration) * 100 : 100;
  const selectedDur = Math.max(0, resolved.end - resolved.start);

  return (
    <div className="space-y-2 select-none">
      <div className="flex items-center justify-between gap-2 text-[11px] text-gray-500 dark:text-gray-400">
        <span>{t('appScreen.videoTrimHint')}</span>
        <span className="tabular-nums font-medium text-gray-700 dark:text-gray-200">
          {formatTrimTime(selectedDur)}
          <span className="text-gray-400 dark:text-gray-500 font-normal">
            {' '}
            / {formatTrimTime(Math.min(maxSelectableSeconds, duration || maxSelectableSeconds))}
          </span>
        </span>
      </div>

      <div
        ref={trackRef}
        className="relative h-16 rounded-lg overflow-hidden bg-neutral-900 touch-none"
        onPointerDown={e => {
          if (e.button !== 0) return;
          const t = clientXToTime(e.clientX);
          const distStart = Math.abs(t - resolved.start);
          const distEnd = Math.abs(t - resolved.end);
          const el = trackRef.current;
          const pxPerSec = el && duration > 0 ? el.getBoundingClientRect().width / duration : 0;
          const hitSec = pxPerSec > 0 ? HANDLE_HIT_PX / pxPerSec : 0.4;
          if (distStart <= hitSec && distStart <= distEnd) {
            beginDrag('start', e.clientX);
            e.currentTarget.setPointerCapture?.(e.pointerId);
            return;
          }
          if (distEnd <= hitSec) {
            beginDrag('end', e.clientX);
            e.currentTarget.setPointerCapture?.(e.pointerId);
            return;
          }
          if (t >= resolved.start && t <= resolved.end) {
            beginDrag('window', e.clientX);
            e.currentTarget.setPointerCapture?.(e.pointerId);
          }
        }}
      >
        {/* Filmstrip */}
        <div className="absolute inset-0 flex">
          {frames.length > 0
            ? frames.map((f, i) => (
                <div key={i} className="flex-1 min-w-0 h-full overflow-hidden">
                  <img src={f} alt="" className="w-full h-full object-cover opacity-90" draggable={false} />
                </div>
              ))
            : (
              <div className="flex-1 h-full bg-gradient-to-r from-neutral-800 via-neutral-700 to-neutral-800" />
            )}
        </div>

        {loadingFrames ? (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
            <div className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
          </div>
        ) : null}

        {/* Dim outside selection */}
        <div className="absolute inset-y-0 left-0 bg-black/55 pointer-events-none" style={{ width: `${startPct}%` }} />
        <div className="absolute inset-y-0 right-0 bg-black/55 pointer-events-none" style={{ width: `${100 - endPct}%` }} />

        {/* Selection window */}
        <div
          className="absolute inset-y-0 border-y-2 border-white pointer-events-none"
          style={{ left: `${startPct}%`, width: `${Math.max(0, endPct - startPct)}%` }}
        >
          {/* Left handle */}
          <div className="absolute inset-y-0 left-0 w-3 -translate-x-1/2 flex items-center justify-center pointer-events-none">
            <div className="h-full w-1.5 rounded-full bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.35)]" />
          </div>
          {/* Right handle */}
          <div className="absolute inset-y-0 right-0 w-3 translate-x-1/2 flex items-center justify-center pointer-events-none">
            <div className="h-full w-1.5 rounded-full bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.35)]" />
          </div>
          {/* Duration chip */}
          <div className="absolute left-1/2 top-1 -translate-x-1/2 px-1.5 py-0.5 rounded bg-black/70 text-[10px] font-semibold text-white tabular-nums whitespace-nowrap">
            {formatTrimTime(selectedDur)}
          </div>
        </div>
      </div>

      <div className="flex justify-between text-[10px] tabular-nums text-gray-400 dark:text-gray-500">
        <span>{formatTrimTime(resolved.start)}</span>
        <span>{formatTrimTime(resolved.end)}</span>
      </div>
    </div>
  );
}
